import { combineReducers } from 'redux';
import {
  isObjectsEqual,
  getSwVersionAndTagVersion,
  getDeviceIndexByID,
  isArraysEqual,
  getUserTag
} from 'common/utils/helpers';
import ActionTypes from 'redux/ActionTypes';
import { deviceDisplayTypes, Status, DEFAULT_TAB_FILTER, DEVICES_PAGE_TAB } from 'common/utils/constants';
import alertsReducer from './Alerts/reducer';
import { userPreferencesReducer } from './UserPreferences';
import { commissioningGA2Reducer } from './Commissioning';
import { isGa2Device } from 'common/utils/deviceTwinUtils';

export const MapListViewSwitchStates = {
  SPLIT: 0,
  LIST: 1,
  GRID: 2
};

export const ConfigHistoryViewSwitchStates = {
  SPLIT: 0,
  CHART: 1,
  LIST: 2
};

function userReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.SET_USER_INFO:
      const userTag = getUserTag(action.userInfo.name);
      return { ...action.userInfo, userTag };
    default:
      return state;
  }
}

function rolePermissionsReducer(state = defaultRolePermissionsState, action) {
  switch (action.type) {
    case ActionTypes.SET_USER_PERMISSIONS:
      return {
        ...state,
        permissions: action.permissions
      };
    case ActionTypes.SET_USER_ROLES:
      return {
        ...state,
        roles: action.roles
      };

    default:
      return state;
  }
}

const defaultRolePermissionsState = {
  roles: null,
  permissions: null
};

// default area to load devices on the map when first loading
const DEFAULT_POLYGON = [
  [-121.44736529431219, 47.85832384070595],
  [-122.94287921032783, 47.85832384070595],
  [-122.94287921032783, 47.416007701509386],
  [-121.44736529431219, 47.416007701509386],
  [-121.44736529431219, 47.85832384070595]
];

const DEFAULT_TAB_DATA = {
  deviceTable: { devices: [] },
  parametersTable: { devices: [] },
  deviceMap: { devices: [], polygon: DEFAULT_POLYGON },
  parametersGraph: { aggregations: [] },
  initialRequestSent: false
};

const DATA_COMPONENT = {
  DEVICE_TABLE: 0,
  DEVICE_MAP: 1,
  PARAM_TABLE: 2,
  PARAM_GRAPH: 3
};

const SORT_DATA_COMPONENT = {
  DEVICE_TABLE_SORT: 0,
  PARAM_TABLE_SORT: 1
};

const getTabDataState = (tab, state, action) => {
  switch (tab) {
    case DATA_COMPONENT.DEVICE_TABLE:
      return {
        ...state,
        deviceTable: {
          ...state.deviceTable,
          devices: action.shouldConcat != null ? state.deviceTable.devices.concat(action.devices) : action.devices,
          continuationToken: action.continuationToken,
          filterRequestIndex: action.filterRequestIndex,
          hasMoreResults: action.hasMoreResults
        }
      };
    case DATA_COMPONENT.DEVICE_MAP:
      return {
        ...state,
        deviceMap: {
          ...state.deviceMap,
          devices: action.shouldConcat != null ? [...state.deviceMap.devices, ...action.devices] : action.devices,
          continuationToken: action.continuationToken,
          filterRequestIndex: action.filterRequestIndex,
          hasMoreRensults: action.hasMoreResults,
          polygon: action.polygon || DEFAULT_POLYGON
        }
      };
    case DATA_COMPONENT.PARAM_TABLE:
      return {
        ...state,
        parametersTable: {
          ...state.parametersTable,
          devices:
            action.shouldConcat != null ? [...state.parametersTable.devices, ...action.devices] : [...action.devices],
          continuationToken: action.continuationToken,
          filterRequestIndex: action.filterRequestIndex,
          hasMoreResults: action.hasMoreResults
        }
      };
    case DATA_COMPONENT.PARAM_GRAPH:
      return {
        ...state,
        parametersGraph: {
          ...state.parametersGraph,
          filterRequestIndex: action.filterRequestIndex,
          aggregations: action.aggregations
        }
      };
    default:
      return {};
  }
};

const getTabSortState = (sortComponent, state, action) => {
  switch (sortComponent) {
    case SORT_DATA_COMPONENT.DEVICE_TABLE_SORT:
      return {
        ...state,
        deviceTable: {
          ...state.deviceTable,
          sort: action.sort
        }
      };
    case SORT_DATA_COMPONENT.PARAM_TABLE_SORT:
      return {
        ...state,
        parametersTable: {
          ...state.parametersTable,
          sort: action.sort
        }
      };
    default:
      return {};
  }
};

function pivotTabDeviceSetsReducer(state = DEFAULT_TAB_DATA, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_DATA_PIVOT_TAB:
      let actionCopy = { ...action };
      actionCopy.devices = action.devices.filter((device) => device.bundleId || isGa2Device(device));
      return getTabDataState(DATA_COMPONENT.DEVICE_TABLE, state, actionCopy);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICEMAP_DATA_PIVOT_TAB:
      return getTabDataState(DATA_COMPONENT.DEVICE_MAP, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_DATA_PIVOT_TAB:
      let paramActionCopy = { ...action };
      let members = [];
      paramActionCopy.devices.forEach((device) => {
        if (device?.tags?.bundle) {
          members.push(device);
        }
      });
      paramActionCopy.devices = members;
      return getTabDataState(DATA_COMPONENT.PARAM_TABLE, state, paramActionCopy);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSGRAPH_PIVOT_TAB:
      return getTabDataState(DATA_COMPONENT.PARAM_GRAPH, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_SORT_PIVOT_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.DEVICE_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_SORT_PIVOT_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.PARAM_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_INITIAL_REQUEST_PIVOT_TAB:
      return {
        ...state,
        initialRequestSent: true
      };
    default:
      return state;
  }
}

function echoTabDeviceSetsReducer(state = DEFAULT_TAB_DATA, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_DATA_ECHO_TAB:
      return getTabDataState(DATA_COMPONENT.DEVICE_TABLE, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICEMAP_DATA_ECHO_TAB:
      return getTabDataState(DATA_COMPONENT.DEVICE_MAP, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_DATA_ECHO_TAB:
      return getTabDataState(DATA_COMPONENT.PARAM_TABLE, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSGRAPH_ECHO_TAB:
      return getTabDataState(DATA_COMPONENT.PARAM_GRAPH, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_SORT_ECHO_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.DEVICE_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_SORT_ECHO_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.PARAM_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_INITIAL_REQUEST_ECHO_TAB:
      return {
        ...state,
        initialRequestSent: true
      };
    default:
      return state;
  }
}

function allDevicesTabDeviceSetsReducer(state = DEFAULT_TAB_DATA, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_DATA_ALL_TAB:
      return getTabDataState(DATA_COMPONENT.DEVICE_TABLE, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICEMAP_DATA_ALL_TAB:
      return getTabDataState(DATA_COMPONENT.DEVICE_MAP, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_DATA_ALL_TAB:
      let paramActionCopy = { ...action };
      let members = [];
      paramActionCopy.devices.forEach((device) => {
        if (device.bundleId && device.members) {
          device.members.forEach((member) => members.push(member));
        } else {
          members.push(device);
        }
      });
      paramActionCopy.devices = members;
      return getTabDataState(DATA_COMPONENT.PARAM_TABLE, state, paramActionCopy);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSGRAPH_ALL_TAB:
      return getTabDataState(DATA_COMPONENT.PARAM_GRAPH, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_DEVICETABLE_SORT_ALL_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.DEVICE_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_PARAMETERSTABLE_SORT_ALL_TAB:
      return getTabSortState(SORT_DATA_COMPONENT.PARAM_TABLE_SORT, state, action);
    case ActionTypes.UPDATE_DEVICE_SETS_INITIAL_REQUEST_ALL_TAB:
      return {
        ...state,
        initialRequestSent: true
      };
    default:
      return state;
  }
}

function devicesStorageReducer(state = { storedDevicesDictionary: {} }, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICES_DICTIONARY:
      let resultDictionary = {};
      resultDictionary = { ...state.storedDevicesDictionary, ...action.localDeviceDictionary };

      return {
        ...state,
        storedDevicesDictionary: resultDictionary
      };

    default:
      return state;
  }
}

function bundlesReducer(state = { storedBundlesDictionary: {} }, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_BUNDLES_DICTIONARY:
      let comboDictionary = {};
      comboDictionary = { ...state.storedBundlesDictionary, ...action.localBundlesDictionary };

      return {
        ...state,
        storedBundlesDictionary: comboDictionary
      };

    default:
      return state;
  }
}

function devicesIdsReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICES_IDS:
      return action.devicesIds;
    default:
      return state;
  }
}

function pivotTabFilterDictionaryReducer(state = DEFAULT_TAB_FILTER.pivot, action) {
  // UPDATE_STATUS
  switch (action.type) {
    case ActionTypes.SET_FILTER_LIST_PIVOT_TAB:
      return { ...state.pivotFilterDictionary, ...action.filterDictionary, ...DEFAULT_TAB_FILTER.pivot };
    default:
      return state;
  }
}

function echoTabFilterDictionaryReducer(state = DEFAULT_TAB_FILTER.echo, action) {
  switch (action.type) {
    case ActionTypes.SET_FILTER_LIST_ECHO_TAB:
      return { ...state.echoFilterDictionary, ...action.filterDictionary, ...DEFAULT_TAB_FILTER.echo };
    default:
      return state;
  }
}

function allDevicesTabFilterDictionaryReducer(state = DEFAULT_TAB_FILTER.all, action) {
  switch (action.type) {
    case ActionTypes.SET_FILTER_LIST_ALL_TAB:
      return { ...state.allDevicesFilterDictionary, ...action.filterDictionary };
    default:
      return state;
  }
}

function localNameFilterReducer(state = '', action) {
  switch (action.type) {
    case ActionTypes.SET_LOCAL_NAME_FILTER:
      return action.string;
    default:
      return state;
  }
}

function historicalEventLogsReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.STORE_HISTORICAL_EVENTLOGS:
      return action.historicalEventLogs;
    default:
      return state;
  }
}

function liveEventLogsReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.STORE_LIVE_EVENTLOGS:
      return action.liveEventLogs;
    default:
      return state;
  }
}

function selectedDevicesDataPivotTabReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_SELECTED_DEVICES_DATA_PIVOT_TAB:
      return { ...state, ...action.selectedDevicesData };
    case ActionTypes.REMOVE_SELECTED_DEVICE_DATA_PIVOT_TAB:
      let devicesDataDicitonary = state;
      delete devicesDataDicitonary[action.deviceKey];
      return devicesDataDicitonary;
    case ActionTypes.CLEAR_SELECTED_DEVICES_DATA_PIVOT_TAB:
      return {};
    default:
      return state;
  }
}

function selectedDevicesDataEchoTabReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_SELECTED_DEVICES_DATA_ECHO_TAB:
      return { ...state, ...action.selectedDevicesData };
    case ActionTypes.REMOVE_SELECTED_DEVICE_DATA_ECHO_TAB:
      let devicesDataDicitonary = state;
      delete devicesDataDicitonary[action.deviceKey];
      return devicesDataDicitonary;
    case ActionTypes.CLEAR_SELECTED_DEVICES_DATA_ECHO_TAB:
      return {};
    default:
      return state;
  }
}

function selectedDevicesDataAlldevicesTabReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_SELECTED_DEVICES_DATA_ALL_TAB:
      return { ...state, ...action.selectedDevicesData };
    case ActionTypes.REMOVE_SELECTED_DEVICE_DATA_ALL_TAB:
      let devicesDataDicitonary = state;
      delete devicesDataDicitonary[action.deviceKey];
      return devicesDataDicitonary;
    case ActionTypes.CLEAR_SELECTED_DEVICES_DATA_ALL_TAB:
      return {};
    default:
      return state;
  }
}

function remoteCommissioningReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_SUPPORTED_COMMISSIONING_WEBSITE_VERSIONS:
      return { ...state, supportedWebsiteVersions: action.versions };
    default:
      return state;
  }
}

function linkedDevicesReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_LINKED_DEVICES:
      return { ...state, pivotDictionary: action.pivotDictionary };
    default:
      return state;
  }
}

function alarmsReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.STORE_ALL_DEVICE_ALARMS:
      if (state && state.deviceAlarms) {
        Object.keys(action.deviceAlarms).forEach((deviceKey) => {
          if (
            state.deviceAlarms[deviceKey] != null &&
            Object.keys(state.deviceAlarms[deviceKey]).length === Object.keys(action.deviceAlarms[deviceKey]).length
          ) {
            for (let i = 0; i < Object.keys(action.deviceAlarms[deviceKey]).length; i++) {
              if (!isObjectsEqual(state.deviceAlarms[deviceKey][i], action.deviceAlarms[deviceKey][i])) {
                return;
              }
            }
            action.deviceAlarms[deviceKey] = state.deviceAlarms[deviceKey];
          }
        });
      }
      return {
        ...state,
        deviceAlarms: action.deviceAlarms
      };
    default:
      return state;
  }
}

function defaultSWListReducer(state = {}, action) {
  if (action.type === ActionTypes.GET_DEFAULT_SW_VERSION) {
    return {
      value: action.value
    };
  } else if (action.type === ActionTypes.UPDATE_DEFAULT_SW_VERSION) {
    return {
      value: action.value
    };
  }
  return state;
}

function allBeamTablesReducer(state = {}, action) {
  if (action.type === ActionTypes.GET_ALL_BEAM_TABLES) {
    return {
      value: action.value
    };
  }
  return state;
}

function actionsListReducer(
  state = {
    active: {},
    past: {}
  },
  action
) {
  if (action.type === ActionTypes.SET_ACTIONS_LIST) {
    return {
      active: { ...state.active, ...action.activeList },
      past: { ...state.past, ...action.pastList }
    };
  } else if (action.type === ActionTypes.UPDATE_ACTIONS_LIST) {
    let newState = { active: { ...state.active }, past: { ...state.past } };
    if (action.isActive) {
      newState.active[action.action.instance_id] = action.action;
    } else {
      newState.past[action.action.instance_id] = action.action;
    }
    return newState;
  } else if (action.type === ActionTypes.CLEAR_ACTIONS_LIST) {
    return {
      active: {},
      past: {}
    };
  } else if (action.type === ActionTypes.REMOVE_ACTION_FROM_LIST) {
    let actionStatus = action.actionStatus;
    let originalList = actionStatus && state[actionStatus];
    let newList = originalList && JSON.parse(JSON.stringify(originalList));
    delete newList[action.actionId];

    return {
      active: actionStatus === 'active' ? newList : state.active,
      past: actionStatus === 'past' ? newList : state.past
    };
  } else if (action.type === ActionTypes.UPDATE_ACTIONS_LIST_FOR_COMMISSION) {
    return {
      active: action.activeList,
      past: action.pastList
    };
  } else if (action.type === ActionTypes.UPDATE_ACTIONS_LIST_FOR_TWO_HOP) {
    return {
      active: action.activeList,
      past: action.pastList
    };
  }
  return state;
}

function softwareUpdateStatusUrlReducer(state = {}, action) {
  if (action.type === ActionTypes.SET_SOFTWARE_UPDATE_STATUS_URL) {
    return { ...state, [action.id]: action.url };
  }
  return state;
}

function softwareVersionsReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_AVAILABLE_SW_VERSIONS:
      /* Software version data will be stored as an object with the device_type as the key,
               and a list of software releases for that device_type as the value. The list of
               software versions will be in order from newest to oldest*/
      let newState = {}; //need to rebuild this list from scratch to account for things being deleted
      let list = action.versions;
      if (list == null || list.length == null || list.length < 1) {
        return newState;
      }
      for (let i = 0; i < list.length; i++) {
        try {
          let entry = list[i];
          const detailedVersion = getSwVersionAndTagVersion(entry.sw_version);
          entry.tag_version = detailedVersion.tag_version;
          if (entry.device_type == null || entry.device_type.length < 1) {
            entry.device_type = ['all'];
          } else {
            entry.device_type.push('all');
          }

          for (let k = 0; k < entry.device_type.length; k++) {
            if (newState[entry.device_type[k]] == null || newState[entry.device_type[k]].length < 1) {
              //store numeric version numbers for faster sorting when adding to list
              newState[entry.device_type[k]] = [
                {
                  ...entry,
                  version_numbers: detailedVersion.sw_version.split('.').map((str) => parseInt(str))
                }
              ];
            } else {
              //put sw version entries into list in numeric order
              let supportedDeviceTypes = entry.supported_device_types;
              let version_numbers = detailedVersion.sw_version.split('.').map((str) => parseInt(str));
              entry.version_numbers = version_numbers;
              let tag_version = detailedVersion.tag_version;
              let j;
              for (j = 0; j < newState[entry.device_type[k]].length; j++) {
                let entry2 = newState[entry.device_type[k]][j];
                let supportedDeviceTypes2 = entry2.supported_device_types;
                if (
                  version_numbers[0] > entry2.version_numbers[0] ||
                  (version_numbers[0] >= entry2.version_numbers[0] && version_numbers[1] > entry2.version_numbers[1]) ||
                  (version_numbers[0] >= entry2.version_numbers[0] &&
                    version_numbers[1] >= entry2.version_numbers[1] &&
                    version_numbers[2] >= entry2.version_numbers[2])
                ) {
                  if (
                    version_numbers[0] === entry2.version_numbers[0] &&
                    version_numbers[1] === entry2.version_numbers[1] &&
                    version_numbers[2] === entry2.version_numbers[2] &&
                    tag_version === entry2.tag_version &&
                    isArraysEqual(supportedDeviceTypes, supportedDeviceTypes2, true)
                  ) {
                    //duplicate entry that has the same version number and tag version, update with new info
                    entry2 = entry;
                    break;
                  }
                  //not a duplicate entry, insert new sw entry at this index
                  newState[entry.device_type[k]].splice(j, 0, entry);
                  break;
                }
              }
              if (j === newState[entry.device_type[k]].length) {
                //checked all entries, this one is the smallest. add to end of list
                newState[entry.device_type[k]].push(entry);
              }
            }
          }
        } catch (error) {
          console.log('error parsing software version:', list[i]);
        }
      }
      return newState;
    default:
      return state;
  }
}

/* In the data tree of the store, the software update status field holds results from polling the status url for each device*/
function softwareUpdateStatusReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SET_SOFTWARE_UPDATE_STATUS:
      let statusWithVersion = action.status;
      let runtimeStatus = action.runtimeStatus;
      if (statusWithVersion != null) statusWithVersion.version = action.version;
      if (runtimeStatus != null) statusWithVersion.runtimeStatus = runtimeStatus;
      return { ...state, [action.id]: statusWithVersion };
    default:
      return state;
  }
}

function rasterDeviceProgressReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_RASTER_PROGRESS:
      return { ...state, [action.id]: { progress: action.progress, statusQueryURL: action.statusQueryURL } };
    case ActionTypes.CLEAR_RASTER_PROGRESS:
      return { ...state, [action.id]: null };
    default:
      return state;
  }
}

function deviceHistoryReducer(state = {}, action, devices) {
  switch (action.type) {
    case ActionTypes.SAVE_DEVICE_TO_HISTORY:
      let newState = {};
      Object.keys(state).forEach((key) => {
        newState[key] = state[key];
      });

      // TODO: Handle -1 as index case, return an error?
      let device = devices?.[getDeviceIndexByID(action.id, devices)];
      if (newState[action.id] == null) {
        newState[action.id] = [];
      }

      if (device === undefined || device === null) {
        device = action.device;
      }

      if (action?.parameters !== null && device !== undefined) {
        device.parameters = action.parameters;
      }

      newState[action.id].splice(0, 0, { ...device }); //insert a copy of the device into the first spot of the history
      return newState;
    default:
      return state;
  }
}

function deviceParameterSchemaReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.DEVICE_PARAMETER_SCHEMA:
      return action.schema;
    default:
      return state;
  }
}

function sharedPagesUIReducer(state = { dropModals: {}, toastNotifications: {}, statusNotification: null }, action) {
  switch (action.type) {
    case ActionTypes.UI_SET_DROP_MODAL_OPEN:
      let dropModals = { ...state.dropModals };
      dropModals[action.name] = action.isOpen;
      return { ...state, dropModals: dropModals };
    case ActionTypes.SET_STATUS_NOTIFICATION:
      return { ...state, statusNotification: action.statusNotification };
    case ActionTypes.UPDATE_STATUS_NOTIFICATION:
      if (action.statusNotification?.id != null && action.statusNotification.id === state.statusNotification?.id) {
        if (state.statusNotification.mode === 'multi') {
          return {
            ...state,
            statusNotification: {
              ...state.statusNotification,
              actions: Object.entries(action.statusNotification.actions).reduce(
                (acc, [id, statusNotification]) => {
                  if (acc[id]) {
                    acc[id] = { ...acc[id], ...statusNotification };
                  }

                  return acc;
                },
                { ...state.statusNotification.actions }
              )
            }
          };
        }

        return { ...state, statusNotification: { ...state.statusNotification, ...action.statusNotification } };
      }

      return state;
    case ActionTypes.DELETE_STATUS_NOTIFICATION:
      if (action.statusNotification?.id == null || action.statusNotification.id === state.statusNotification?.id) {
        return { ...state, statusNotification: null };
      }

      return state;
    case ActionTypes.ADD_TOAST_NOTIFICATION:
      let toastsForAddAction = JSON.parse(JSON.stringify(state.toastNotifications));
      toastsForAddAction[action?.toastNotification?.id] = action.toastNotification;
      return { ...state, toastNotifications: toastsForAddAction };
    case ActionTypes.DELETE_TOAST_NOTIFICATION:
      let toastsForDeleteAction = JSON.parse(JSON.stringify(state.toastNotifications));
      delete toastsForDeleteAction[action?.toastNotificationId];
      return { ...state, toastNotifications: toastsForDeleteAction };
    case ActionTypes.CLEAR_TOAST_NOTIFICATIONS:
      return { ...state, toastNotifications: {} };
    case ActionTypes.UI_SET_SRT_LASTSCROLL:
      return { ...state, [action.tableId]: action.scrollDepth };
    default:
      return state;
  }
}

function singleDevicePageUIReducer(state = defaultSingleDevicePageUIState, action) {
  switch (action.type) {
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_SELECTED_DEVICE:
      return { ...state, selectedDevice: action.device };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_PATCH_VALUES:
      let patchValues = { ...state.patchValues, ...action.patchValues };
      let keys = Object.keys(patchValues);
      for (let i = 0; i < keys.length; i++) {
        if (patchValues[keys[i]] === null) {
          delete patchValues[keys[i]];
        }
      }
      return { ...state, patchValues: patchValues };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CLEAR_PATCH_VALUES:
      return { ...state, patchValues: {} };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_SET_SW_VERSION_MODAL_OPEN:
      return { ...state, swVersionModalOpen: action.isOpen };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIG_CHART_MODAL_OPEN:
      return { ...state, configChartModalOpen: action.isOpen };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_SET_SEARCH_BAR_INDEX:
      return { ...state, searchBarIndex: action.index };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CHART_SELECTIONS_SAVE:
      return { ...state, savedSelectionsConfig: action.savedSelectionsConfig };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_SELECTED_EVENTLOG_INDEX:
      return {
        ...state,
        selectedEventLogIndexObj: { ...state.selectedEventLogIndex, [action.deviceId]: action.selectedEventLogIndex }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_DISPLAY_LIVE_LOGS:
      return { ...state, displayLiveLogs: action.displayLiveLogs };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_LOCAL_LOG_LIST:
      return { ...state, localLogList: action.localLogList };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORYVIEW_SWITCH_STATE:
      // return {...state, configHisotryViewSwitchState: action.configHisotryViewSwitchState};
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: {
            ...state.configHistory[action.deviceId],
            configHisotryViewSwitchState: action.configHisotryViewSwitchState
          }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_KPISCHECKBOXITEMS:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: { ...state.configHistory[action.deviceId], kpisCheckboxItems: action.kpisCheckboxItems }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_PARAMCHECKBOXITEMS:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: { ...state.configHistory[action.deviceId], paramCheckboxItems: action.paramCheckboxItems }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_LOCALCOUNTERSDATA:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: { ...state.configHistory[action.deviceId], localCountersData: action.localCountersData }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_SELECTEDCOUNTERS:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: { ...state.configHistory[action.deviceId], selectedCounters: action.selectedCounters }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_SAVEDDATACONFIGS:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: { ...state.configHistory[action.deviceId], savedDataConfigs: action.savedDataConfigs }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_CONFIGHISTORY_SAVED_CHART_DATA_CONFIGS:
      return {
        ...state,
        configHistory: {
          ...state.configHistory,
          [action.deviceId]: {
            ...state.configHistory[action.deviceId],
            savedChartDataConfigs: action.savedChartDataConfigs
          }
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_UPDATE_SELECTED_RASTER_ID:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectedRasterId: action.selectedRasterId
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_UPDATE_SELECTED_DEVICE_HEATMAP_ROW_IDX:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectedRowIdx: action.rowIdx
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_UPDATE_SELECTED_RASTER_RECORD:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectedRasterRecord: action.record
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_UPDATE_SELECTED_BEAMRANK_IDX:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectedBeamRankIndex: action.index
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_UPDATE_SELECTED_RASTERS_DICTIONARY:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectedRastersDictionary: action.selectedRastersDictionary
        }
      };
    case ActionTypes.UI_SINGLE_DEVICE_PAGE_HEATMAP_LIBRARY_TABLE_SET_SELECT_ALL:
      return {
        ...state,
        heatmap: {
          ...state.heatmap,
          selectAll: action.isSelected
        }
      };
    default:
      return state;
  }
}

function pivotOverviewPageUIReducer(state = defaultPivotOverviewPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UI_PIVOT_OVERVIEW_PAGE_UPDATE_SELECTED_DEVICES:
      return { ...state, selectedDevices: action.devices };
    case ActionTypes.UI_PIVOT_OVERVIEW_PAGE_UPDATE_SINGLE_SELECTED_DEVICE:
      return { ...state, selectedDevices: { ...state.selectedDevices, [action.device.deviceId]: action.device } };
    case ActionTypes.UI_PIVOT_OVERVIEW_PAGE_UPDATE_ACTIVE_TAB:
      return { ...state, activeTab: action.deviceId}
    case ActionTypes.UI_PIVOT_OVERVIEW_PAGE_UPDATE_RF_CONFIG_ACTION_STATUS:
      return { ...state, actions: { ...state.actions, rfConfig: { [action.deviceId]: { status: action.status } } } };
    default:
      return state;
  }
}

function echoOverviewPageUIReducer(state = defaultEchoOverviewPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UI_ECHO_OVERVIEW_PAGE_UPDATE_SELECTED_DEVICE:
      return { ...state, selectedDevice: action.device };
    default:
      return state;
  }
}

function chartExplorerPageUIReducer(state = defaultChartExplorerPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UI_CHART_SET_SELECT:
      return { ...state, preferenceId: action.meta.preferenceId, state: null };
    case ActionTypes.UI_CHART_SET_UPDATE:
      return state.preferenceId === action.meta.preferenceId ? { ...state, state: action.payload } : state;
    default:
      return state;
  }
}

function devicesPageUIReducer(state = defaultDevicesPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICES_PAGE_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.tab
      };
    case ActionTypes.UI_DEVICESPAGE_MAPLISTVIEW_SWITCH_STATUS_PIVOT_TAB:
      return {
        ...state,
        pivotTab: { ...state.pivotTab, mapListViewSwitchState: action.mapListViewSwitchState }
      };
    case ActionTypes.UI_DEVICESPAGE_MAPLISTVIEW_SWITCH_STATUS_ECHO_TAB:
      return {
        ...state,
        echoTab: { ...state.echoTab, mapListViewSwitchState: action.mapListViewSwitchState }
      };
    case ActionTypes.UI_DEVICESPAGE_MAPLISTVIEW_SWITCH_STATUS_ALL_TAB:
      return {
        ...state,
        allDevicesTab: { ...state.allDevicesTab, mapListViewSwitchState: action.mapListViewSwitchState }
      };
    case ActionTypes.UI_DEVICESPAGE_SHOW_SELECT_LIST_NOTIFICATION:
      return {
        ...state,
        showSelectListNotification: action.show
      };
    case ActionTypes.UI_DEVICESPAGE_SORT_ALARMS:
      return {
        ...state,
        sortAlarmsBy: action.sortBy
      };
    case ActionTypes.SET_ACTIVE_POLYGON:
      return {
        ...state,
        activePolygonId: action.setPolygonId
      };
    case ActionTypes.SET_DEFAULT_ACTIVE_POLYGON:
      return {
        ...state,
        activePolygonId: action.payload.activePolygonId,
        isDefaultPolygonInitialised: action.payload.isDefaultPolygonInitialised
      };
    case ActionTypes.UI_DEVICESPAGE_SET_MAP_SELECT:
      if (action.activeTab === DEVICES_PAGE_TAB.pivot) {
        return {
          ...state,
          pivotTab: { ...state.pivotTab, selectMode: { ...state.pivotTab.selectMode, ...action.selectMode } },
          lastSelection: null
        };
      } else if (action.activeTab === DEVICES_PAGE_TAB.echo) {
        return {
          ...state,
          echoTab: { ...state.echoTab, selectMode: { ...state.echoTab.selectMode, ...action.selectMode } },
          lastSelection: null
        };
      } else {
        return {
          ...state,
          allDevicesTab: {
            ...state.allDevicesTab,
            selectMode: { ...state.allDevicesTab.selectMode, ...action.selectMode }
          },
          lastSelection: null
        };
      }
    // return { ...state, selectMode: { ...state.selectMode, ...action.selectMode }, lastSelection: null };
    case ActionTypes.UI_DEVICESPAGE_MAPLISTVIEW_MAP_VIEWPORT:
      return { ...state, mapZoom: action.zoom, mapLocation: action.center };
    case ActionTypes.UI_DEVICESPAGE_MAP_LAST_SELECTION:
      return { ...state, lastSelection: action.mapDrawnItem };
    case ActionTypes.UI_DEVICESPAGE_MAP_DRAWINGS:
      return { ...state, mapDrawnItems: action.mapDrawnItems };
    case ActionTypes.UI_DEVICESPAGE_SELECT_DEVICE_ID:
      return { ...state, selectedDeviceId: action.id };
    case ActionTypes.UI_DEVICESPAGE_EXPANDED_BUNDLE_ID:
      return { ...state, expandedBundleId: action.bundleId };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_SELECTED_DEVICES_LIST_PIVOT_TAB:
      let prevPivotList = state.pivotTab.selectedDevicesDictionary;
      return {
        ...state,
        pivotTab: { ...state.pivotTab, selectedDevicesDictionary: { ...prevPivotList, ...action.selectedDevices } }
      };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_SELECTED_DEVICES_LIST_ECHO_TAB:
      let prevEchoList = state.echoTab.selectedDevicesDictionary;
      return {
        ...state,
        echoTab: { ...state.pivotTab, selectedDevicesDictionary: { ...prevEchoList, ...action.selectedDevices } }
      };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_SELECTED_DEVICES_LIST_ALL_TAB:
      let prevAllList = state.allDevicesTab.selectedDevicesDictionary;
      return {
        ...state,
        allDevicesTab: {
          ...state.allDevicesTab,
          selectedDevicesDictionary: { ...prevAllList, ...action.selectedDevices }
        }
      };
    case ActionTypes.UI_DEVICESPAGE_CLEAR_DEVICE_INDEX_LIST_PIVOT_TAB:
      return { ...state, pivotTab: { ...state.pivotTab, selectedDevicesDictionary: {} } };
    case ActionTypes.UI_DEVICESPAGE_CLEAR_DEVICE_INDEX_LIST_ECHO_TAB:
      return { ...state, echoTab: { ...state.echoTab, selectedDevicesDictionary: {} } };
    case ActionTypes.UI_DEVICESPAGE_CLEAR_DEVICE_INDEX_LIST_ALL_TAB:
      return { ...state, allDevicesTab: { ...state.allDevicesTab, selectedDevicesDictionary: {} } };
    case ActionTypes.UI_DEVICESPAGE_SET_BULK_ACTION_MODAL_OPEN:
      return { ...state, bulkActionModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_BULK_VIEW_MODAL_OPEN:
      return { ...state, bulkViewModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_BULK_CREATE_LIST_MODAL_OPEN:
      return { ...state, bulkCreateDevicesListModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_BULK_MANAGE_LISTS_MODAL_OPEN:
      return { ...state, bulkManageDevicesListsModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_MANAGE_COLUMNS_MODAL_OPEN:
      return { ...state, manageDevicesTableColumnsModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_VIEW_DEVICES_LIST_MODAL_OPEN:
      return { ...state, viewDevicesListModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_SW_UPDATE_STATUS_MODAL_OPEN:
      return { ...state, swUpdateStatusModalOpen: action.isOpen };
    case ActionTypes.UI_DEVICESPAGE_SET_SELECTED_PARAMETERS_PIVOT_TAB:
      return { ...state, pivotTab: { ...state.pivotTab, selectedParameters: action.parameters } };
    case ActionTypes.UI_DEVICESPAGE_SET_SELECTED_PARAMETERS_ECHO_TAB:
      return { ...state, echoTab: { ...state.echoTab, selectedParameters: action.parameters } };
    case ActionTypes.UI_DEVICESPAGE_SET_SELECTED_PARAMETERS_ALL_TAB:
      return { ...state, allDevicesTab: { ...state.allDevicesTab, selectedParameters: action.parameters } };
    case ActionTypes.UI_DEVICESPAGE_SET_DEVICE_DISPLAY_MODE_PIVOT_TAB:
      return { ...state, pivotTab: { ...state.pivotTab, deviceDisplayMode: action.displayMode } };
    case ActionTypes.UI_DEVICESPAGE_SET_DEVICE_DISPLAY_MODE_ECHO_TAB:
      return { ...state, echoTab: { ...state.echoTab, deviceDisplayMode: action.displayMode } };
    case ActionTypes.UI_DEVICESPAGE_SET_DEVICE_DISPLAY_MODE_ALL_TAB:
      return { ...state, allDevicesTab: { ...state.allDevicesTab, deviceDisplayMode: action.displayMode } };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_SELECTED_DEVICE_GROUP_NAME:
      return { ...state, selectedDeviceGroupName: action.selectedDeviceGroupName };
    case ActionTypes.UI_DEVICESPAGE_SET_IS_DEFAULT_DEVICE_GROUP_INITIALIZED:
      return { ...state, isDefaultDeviceGroupInitialized: action.isDefaultDeviceGroupInitialized };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_ALL_USER_DEVICE_GROUPS:
      return { ...state, deviceGroups: action.deviceGroups };
    case ActionTypes.UI_DEVICESPAGE_EDIT_SINGLE_DEVICE_GROUP:
      let copiedDeviceGroups = JSON.parse(JSON.stringify(state.deviceGroups));
      if (action.newDeviceGroup.default) {
        Object.keys(copiedDeviceGroups).forEach((name) => {
          if (name !== action.name) {
            copiedDeviceGroups[name].default = false;
          }
        });
      }
      copiedDeviceGroups[action.name] = action.newDeviceGroup;
      return { ...state, deviceGroups: copiedDeviceGroups };
    case ActionTypes.UI_DEVICESPAGE_UPDATE_ALL_USER_DEVICE_POLYGONS:
      return { ...state, polygons: action.polygons };
    case ActionTypes.UI_DEVICESPAGE_DELETE_USER_DEVICE_POLYGON:
      return { ...state, polygons: state.polygons.filter((polygon) => polygon.deviceGroupId !== action.deviceGroupId) };
    case ActionTypes.UI_USER_PREFERENCE_MANAGE_MODAL_IS_OPEN:
      return { ...state, manageFilterSetsModal: action.isOpen };
    case ActionTypes.UI_USER_PREFERENCE_CREATE_MODAL_IS_OPEN:
      return { ...state, createFilterSetModal: action.isOpen };
    case ActionTypes.UI_USER_PREFERENCE_ACTIVE_FILTER_SET:
      return { ...state, activeFilterSetId: action.preferenceId };
    case ActionTypes.UI_CREATE_SITE_MODAL_OPEN:
      return { ...state, createSiteModalOpen: action.isOpen };
    case ActionTypes.UI_SET_DEVICETABLE_WIDTH:
      return { ...state, deviceTableWidth: action.deviceTableWidth };
    default:
      return state;
  }
}

const defaultActionsPageUIState = {
  twoHopStatusInstanceDictionary: null, //{alignmentId: {rowKey: instanceObj, rowKey2: instanceObj2}}
  twoHopProgressDictionary: null, //{alignmentId: {rowKey: progressInt} }
  twoHopSelectedRastersDictionary: null, //{alignmentId: {rowKey: false, rowKey2: false}}
  twoHopRasterIdDictionary: null, //{alignmentId: {rowKey: rasterId, rowKey2: rasterId2}}
  twoHopScoreDictionary: null, // {alignmentId: {rowKey: score, rowKey2: score2}}
  twoHopRecBeamInfoDictionary: null, //{alignmentId: beamInfoObj}
  twoHopCancelStatusDictionary: null //{alignmentIdL cancelStatusObj}
};

function actionsPageReducer(state = defaultActionsPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_STATUS_INSTANCE_DICTIONARY:
      return {
        ...state,
        twoHopStatusInstanceDictionary: {
          ...state.twoHopStatusInstanceDictionary,
          [action.alignmentId]: action.twoHopStatusInstanceDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_PROGRESS_DICTIONARY:
      return {
        ...state,
        twoHopProgressDictionary: {
          ...state.twoHopProgressDictionary,
          [action.alignmentId]: action.twoHopProgressDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_SELECTED_RASTERS_DICTIONARY:
      return {
        ...state,
        twoHopSelectedRastersDictionary: {
          ...state.twoHopSelectedRastersDictionary,
          [action.alignmentId]: action.twoHopSelectedRastersDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_RASTER_ID_DICTIONARY:
      return {
        ...state,
        twoHopRasterIdDictionary: {
          ...state.twoHopRasterIdDictionary,
          [action.alignmentId]: action.twoHopRasterIdDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_SCORE_DICTIONARY:
      return {
        ...state,
        twoHopScoreDictionary: {
          ...state.twoHopScoreDictionary,
          [action.alignmentId]: action.twoHopScoreDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_REC_BEAM_INFO_DICTIONARY:
      return {
        ...state,
        twoHopRecBeamInfoDictionary: {
          ...state.twoHopRecBeamInfoDictionary,
          [action.alignmentId]: action.twoHopRecBeamInfoDictionary
        }
      };
    case ActionTypes.UPDATE_ACTIONS_PAGE_TWO_HOP_CANCEL_STATUS_DICTIONARY:
      return {
        ...state,
        twoHopCancelStatusDictionary: {
          ...state.twoHopCancelStatusDictionary,
          [action.alignmentId]: action.cancelStatusObj
        }
      };
    default:
      return state;
  }
}

function heatmapHistoryDictionaryReducer(state = {}, action) {
  let newState = { ...state };
  switch (action.type) {
    case ActionTypes.UPDATE_DEVICE_HEATMAP_HISTORY_DICTIONARY:
      newState = { ...state, [action.deviceId]: action.deviceHeatmapHistoryDictionary };
      return newState;
    case ActionTypes.UPDATE_RASTER_RECORD:
      // newState[action.deviceId][action.rasterId] = action.record;
      // undo the changes below when the two-hop API is ready
      if (newState[action.deviceId]) {
        newState[action.deviceId][action.rasterId] = action.record;
      } else {
        newState[`${action.deviceId}`] = {};
        newState[action.deviceId][action.rasterId] = action.record;
      }
      return newState;
    default:
      return state;
  }
}

function rasterSettingsReducer(state = {}, action) {
  switch (action.type) {
    case ActionTypes.GET_RASTER_SETTINGS:
      return action.data;
    default:
      return state;
  }
}

function usersManagementPageUIReducer(state = defaultUsersManagementPageUIState, action) {
  switch (action.type) {
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_USERS:
      return { ...state, filteredUsers: { local: action.local, original: action.original } };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_ROLE_USERS:
      return { ...state, roleUsers: action.roleUsers };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_SEARCH_STRING:
      return { ...state, searchString: action.searchString };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_SELECTED_USER_ID:
      return { ...state, selectedUserId: action.selectedUserId };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_UPDATE_SELECTED_USERS_LIST:
      let prevSelectedUsersList = state.selectedUsersDictionary;
      return { ...state, selectedUsersDictionary: { ...prevSelectedUsersList, ...action.selectedUsersDictionary } };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_UPDATE_SELECTED_USERS_DATA:
      let prevSelectedUsersData = state.selectedUsersData;
      return { ...state, selectedUsersData: { ...prevSelectedUsersData, ...action.selectedUsersData } };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_REMOVE_SELECTED_USER_DATA:
      let copiedSelectedUsersDictionary = JSON.parse(JSON.stringify(state.selectedUsersDictionary));
      delete copiedSelectedUsersDictionary[action.userKey];
      return { ...state, selectedUsersDictionary: copiedSelectedUsersDictionary };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_CLEAR_SELECTED_USERS_LIST:
      return { ...state, selectedUsersDictionary: {} };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_CLEAR_SELECTED_USERS_DATA:
      return { ...state, selectedUsersData: null };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_SET_SELECT_MODE:
      return { ...state, selectMode: { ...state.selectMode, ...action.selectMode } };
    case ActionTypes.UI_USERS_MANAGEMENT_PAGE_ROLE_LIST:
      return { ...state, roleList: action.roleList };
    default:
      return state;
  }
}

function heatmapReducer(
  state = {
    values: null,
    min: 0,
    max: 0,
    beamrank: null,
    activeBeam: null
  },
  action
) {
  if (action.type === ActionTypes.UPDATE_HEATMAP) {
    let newState = { ...state };
    if (action.heatmap != null) {
      newState.values = action.heatmap;
    }
    if (action.max != null) {
      newState.max = action.max;
    }
    if (action.min != null) {
      newState.min = action.min;
    }
    if (action.beamrank != null) {
      newState.beamrank = action.beamrank;
    }
    if (action.activeBeam != null) {
      newState.activeBeam = action.activeBeam;
    }
    return newState;
  }
  return state;
}

let defaultLastScrollDictionary = {
  0: {
    deviceTable: 0,
    parametersTable: 0
  },
  1: {
    deviceTable: 0,
    parametersTable: 0
  },
  2: {
    deviceTable: 0,
    parametersTable: 0
  }
};

function lastScrollDictionaryReducer(state = defaultLastScrollDictionary, action) {
  switch (action.type) {
    case ActionTypes.SET_LAST_SCROLL:
      return { ...state, [action.activeTab]: { ...state[action.activeTab], [action.component]: action.value } };
    default:
      return state;
  }
}

function featureToggleReducer(state = { autoUpdate: null, autoUpdateSchedule: null }, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_FEATURE_TOGGLE:
      return { ...state, autoUpdate: action.autoUpdate, autoUpdateSchedule: action.autoUpdateSchedule };
    default:
      return state;
  }
}

const defaultAutoUpdateSchedule = {
  schedule: {
    hour: 0,
    minute: 0,
    recurrence: 0,
    scheduleType: 'day',
    dayOfWeek: 'Mon',
    dayOfMonth: 0
  },
  nextScheduledRunTime: null,
  scheduleText: null
};

function autoUpdateScheduleReducer(state = defaultAutoUpdateSchedule, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_AUTO_UPDATE_SCHEDULE:
      return { ...state, ...action.autoUpdateSchedule };
    default:
      return state;
  }
}

function productTypesListReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_PRODUCT_TYPES_LIST:
      return action.productTypesList;
    default:
      return state;
  }
}

function factoryListReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_FACTORY_LIST:
      return action.factoryList;
    default:
      return state;
  }
}

function protectedListReducer(state = null, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_PROTECTED_LIST:
      return action.protectedList;
    default:
      return state;
  }
}

const defaultStatusList = {
  searchDevices: Status.NONE,
  auth0: Status.NONE,
  deviceParameterSchema: Status.NONE,
  bundles: null
};

function statusReducer(state = defaultStatusList, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_STATUS:
      return { ...state, [action.name]: action.status };
    case ActionTypes.UPDATE_STATUS_BUNDLE:
      return { ...state, bundles: { ...state.bundles, [action.name]: action.status } };
    default:
      return state;
  }
}

const defaultStyle = {
  isShowScrollReactTable: false,
  juanStyle: false,
  mapSize: 'shrink',
  isBlockedLayerShow: false,
  consolePageSize: { width: 0, height: 0 },
  consolePageClass: ''
};

function styleReducer(state = defaultStyle, action) {
  switch (action.type) {
    case ActionTypes.SET_SCROLL_REACT_TABLE_VISIBILITY:
      return { ...state, isShowScrollReactTable: action.setVisibility };
    case ActionTypes.SET_JUAN_STYLE:
      return { ...state, juanStyle: action.juan };
    case ActionTypes.SET_CONSOLE_PAGE_SIZE:
      return { ...state, consolePageSize: action.size };
    case ActionTypes.SET_MAP_SIZE:
      return { ...state, mapSize: action.setSize };
    case ActionTypes.SET_CONSOLE_PAGE_CLASS:
      return { ...state, consolePageClass: action.className };
    case ActionTypes.SET_BLOCKED_LAYER_SHOW:
      return { ...state, isBlockedLayerShow: action.payload };
    default:
      return state;
  }
}

function pageReducer(state = '', action) {
  if (action.type === ActionTypes.SET_PAGE) {
    return action.page;
  }
  return state;
}

const defaultDevicesTabState = {
  mapListViewSwitchState: MapListViewSwitchStates.SPLIT,
  sortAlarmsBy: '',
  selectedParameters: ['Connection Status'],
  deviceDisplayMode: deviceDisplayTypes.ID,
  selectedDevicesDictionary: {},
  selectMode: { bulk: true, allSelect: false, shape: 'none' }
};

const defaultDevicesPageUIState = {
  activeTab: DEVICES_PAGE_TAB.pivot,
  pivotTab: { ...defaultDevicesTabState, deviceDisplayMode: deviceDisplayTypes.NAME },
  echoTab: { ...defaultDevicesTabState },
  allDevicesTab: { ...defaultDevicesTabState },
  // mapListViewSwitchState: MapListViewSwitchStates.SPLIT,
  // sortAlarmsBy: '',
  mapLocation: [47.619124098197325, -122.1988111272783],
  mapZoom: 10,
  selectMode: { bulk: false, allSelect: false, shape: 'none' },
  lastSelection: null,
  mapDrawnItems: null,
  selectedDeviceId: null,
  multiDeviceIndexList: [],
  bulkActionModalOpen: false,
  bulkViewModalOpen: false,
  bulkCreateDevicesListModalOpen: false,
  bulkManageDevicesListsModalOpen: false,
  manageDevicesTableColumnsModalOpen: false,
  swUpdateStatusModalOpen: false,
  viewDevicesListModalOpen: false,
  polygons: [],
  activePolygonId: null,
  isDefaultPolygonInitialised: false,
  isDefaultDeviceGroupInitialized: false,
  deviceTableWidth: null,
  deviceGroups: {},
  selectedDeviceGroupName: null
  // selectedParameters: ['Connection Status'],
  // deviceDisplayMode: deviceDisplayTypes.ID
};

const defaultSingleDevicePageUIState = {
  selectedDevice: undefined,
  patchValues: {},
  swVersionModalOpen: false,
  searchBarIndex: null,
  //the below default should be pulled in from the backend when availible, for now these are the hard coded defaults
  //chartSelectionConfig: defaultChartSelectionConfig,
  selectedEventLogIndexObj: {},
  displayLiveLogs: false,
  localLogList: null,
  // configHisotryViewSwitchState: 0,
  configHistory: {},
  heatmap: {
    selectedRasterId: null,
    selectedRowIdx: 0,
    selectedRastersDictionary: null,
    heatmapHistoryList: null,
    min: 0,
    max: 0,
    activeBeam: null,
    selectedRasterRecord: null,
    selectedBeamRankIndex: 1,
    selectAll: false
  }
};

const defaultUsersManagementPageUIState = {
  filteredUsers: { local: null, original: null },
  roleUsers: null,
  searchString: '',
  selectedUserId: null,
  selectedUsersDictionary: {},
  selectedUsersData: null,
  selectMode: { allSelect: false },
  roleList: null
};

const defaultPivotOverviewPageUIState = {
  selectedDevices: {},
  activeTab: '',
  actions: {}
};

const defaultEchoOverviewPageUIState = {
  selectedDevice: null
};

const defaultChartExplorerPageUIState = {
  preferenceId: null,
  state: {}
};

const dataReducer = combineReducers({
  user: userReducer,
  rolePermissions: rolePermissionsReducer,
  devicesStorage: devicesStorageReducer,
  // tab-specific data
  pivotDeviceSets: pivotTabDeviceSetsReducer,
  echoDeviceSets: echoTabDeviceSetsReducer,
  allDeviceSets: allDevicesTabDeviceSetsReducer,
  pivotFilterDictionary: pivotTabFilterDictionaryReducer,
  echoFilterDictionary: echoTabFilterDictionaryReducer,
  allDevicesFilterDictionary: allDevicesTabFilterDictionaryReducer,
  devicesIds: devicesIdsReducer,
  bundlesStorage: bundlesReducer,
  localNameFilter: localNameFilterReducer,
  deviceParameterSchema: deviceParameterSchemaReducer,
  deviceHistory: deviceHistoryReducer,
  rasterDeviceProgress: rasterDeviceProgressReducer,
  softwareUpdateStatuses: softwareUpdateStatusReducer,
  softwareUpdateStatusUrls: softwareUpdateStatusUrlReducer,
  softwareVersions: softwareVersionsReducer,
  defaultsList: defaultSWListReducer,
  allBeamTables: allBeamTablesReducer,
  actionsList: actionsListReducer,
  alarms: alarmsReducer,
  linkedDevices: linkedDevicesReducer,
  remoteCommissioning: remoteCommissioningReducer,
  commissioningGA2: commissioningGA2Reducer,
  selectedDevicesDataPivotTab: selectedDevicesDataPivotTabReducer,
  selectedDevicesDataEchoTab: selectedDevicesDataEchoTabReducer,
  selectedDevicesDataAllDevicesTab: selectedDevicesDataAlldevicesTabReducer,
  historicalEventLogs: historicalEventLogsReducer,
  liveEventLogs: liveEventLogsReducer,
  userPreferences: userPreferencesReducer,
  heatmap: heatmapReducer,
  featureToggle: featureToggleReducer,
  autoUpdateSchedule: autoUpdateScheduleReducer,
  productTypesList: productTypesListReducer,
  heatmapHistoryDictionary: heatmapHistoryDictionaryReducer,
  factoryList: factoryListReducer,
  protectedList: protectedListReducer,
  lastScrollDictionary: lastScrollDictionaryReducer,
  rasterSettings: rasterSettingsReducer,
  alerts: alertsReducer
});

const uiReducer = combineReducers({
  sharedPages: sharedPagesUIReducer,
  devicesPage: devicesPageUIReducer,
  actionsPage: actionsPageReducer,
  singleDevicePage: singleDevicePageUIReducer,
  usersManagementPage: usersManagementPageUIReducer,
  pivotOverviewPage: pivotOverviewPageUIReducer,
  echoOverviewPage: echoOverviewPageUIReducer,
  chartExplorerPage: chartExplorerPageUIReducer
});

export const rootReducer = combineReducers({
  data: dataReducer,
  ui: uiReducer,
  statuses: statusReducer,
  style: styleReducer,
  page: pageReducer //keeps track of the active page
});
